// src/components/document-sets/DocumentSetInlineEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { X, Check, TriangleAlert } from 'lucide-react';
import { getRandomColor } from '../../utils/documentSetColors';

/**
 * Custom Icon Button for Inline Editor - matches sidebar pill hover behavior
 */
const InlineEditorIconButton = ({ children, onClick, onMouseDown, title, hoverColor, defaultColor, hoverTextColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="p-1 rounded-app-2xs flex items-center justify-center flex-shrink-0"
      style={{
        backgroundColor: isHovered ? hoverColor : 'transparent',
        color: isHovered ? hoverTextColor : defaultColor,
        opacity: isHovered ? 1 : 0.7
      }}
      title={title}
      aria-label={title}
    >
      {React.cloneElement(children, {
        className: 'w-3 h-3'
      })}
    </button>
  );
};

/**
 * Inline editor for creating or editing document sets
 * Renders as a pill with an input field that's automatically focused
 *
 * FIXED: Uses input instead of textarea to maintain consistent height
 * This prevents layout shifts when switching between pill and editor
 */
const DocumentSetInlineEditor = ({
  onCreateSet,
  onCancel,
  documentSets = [], // Existing sets for name validation
  existingSet = null, // If editing an existing set, pass it here
  onUpdateSet, // Callback for updating existing set
  onFocusChange, // Callback when focus state changes
  autoFocus = true, // Whether to auto-focus the input on mount
  saveOnBlur = true, // Whether to save on blur (default true for sidebar, false for modal)
}) => {
  const [inputValue, setInputValue] = useState(existingSet?.name || '');
  const [color, setColor] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const colorInitialized = useRef(false);

  // Generate random color on mount (or use existing)
  useEffect(() => {
    if (!colorInitialized.current) {
      setColor(existingSet?.color || getRandomColor(documentSets));
      colorInitialized.current = true;
    }
  }, [existingSet, documentSets]);

  // Handle auto-focus separately
  useEffect(() => {
    if (autoFocus && color) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Select all text if editing existing set
          if (existingSet) {
            inputRef.current.select();
          }
        }
      }, 0);
    }
  }, [autoFocus, color, existingSet]);

  // Check if name is valid (non-empty and unique)
  const trimmedValue = inputValue.trim();
  const isDuplicate = documentSets.some(
    set => set.id !== existingSet?.id && set.name.toLowerCase() === trimmedValue.toLowerCase()
  );
  const isValid = trimmedValue && !isDuplicate;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isValid) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSave = async () => {
    if (!isValid || !color) return;

    console.log('DocumentSetInlineEditor: handleSave called', { trimmedValue, color });

    if (existingSet) {
      // Update existing set
      await onUpdateSet?.(existingSet.id, trimmedValue);
    } else {
      // Create new set
      console.log('DocumentSetInlineEditor: calling onCreateSet');
      await onCreateSet(trimmedValue, color);
      console.log('DocumentSetInlineEditor: onCreateSet completed');
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocusChange?.(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    onFocusChange?.(false);

    // Only save/cancel on blur if saveOnBlur is true (sidebar behavior)
    // In modal, blur should do nothing - only Enter or checkmark saves
    if (saveOnBlur) {
      if (isValid) {
        handleSave();
      } else {
        onCancel();
      }
    }
  };

  if (!color) return null;

  return (
    <div
      className="inline-flex items-center rounded-full text-app-xs font-medium max-w-full group border h-[25px]"
      style={{
        backgroundColor: color.bg,
        color: color.text,
        borderColor: color.border || color.bg,
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingLeft: '0.75rem', // px-3
        paddingRight: trimmedValue ? '6px' : '0.75rem', // Reduce right padding when buttons are visible
      }}
    >
      {/* Input field - shrinks to make room for buttons */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Type tag name..."
        className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-current"
        style={{
          color: color.text,
          opacity: inputValue ? 1 : 0.6,
          height: '1.11rem',
          lineHeight: '1.11rem',
        }}
        maxLength={50}
      />

      {/* Action buttons */}
      {trimmedValue && (
        <div className="pl-app-md flex items-center gap-0 flex-shrink-0">
          {isValid ? (
            <InlineEditorIconButton
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur
                handleSave();
              }}
              title={existingSet ? "Save changes (Enter)" : "Create tag (Enter)"}
              hoverColor={color.hover}
              defaultColor={color.text}
              hoverTextColor={color.text}
            >
              <Check />
            </InlineEditorIconButton>
          ) : isDuplicate ? (
            <InlineEditorIconButton
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur - do nothing
              }}
              title="This name is taken by another tag"
              hoverColor={color.hover}
              defaultColor={color.text}
              hoverTextColor={color.text}
            >
              <TriangleAlert />
            </InlineEditorIconButton>
          ) : null}
          <InlineEditorIconButton
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent blur
              onCancel();
            }}
            title="Cancel (Esc)"
            hoverColor={color.hover}
            defaultColor={color.text}
            hoverTextColor={color.text}
          >
            <X />
          </InlineEditorIconButton>
        </div>
      )}
    </div>
  );
};

export default DocumentSetInlineEditor;